/**
 * SubscriptionHub - Unified Subscription, Plans & Storage page
 * Merges pricing, plans, storage usage, and billing into one cohesive experience
 */

import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloud,
  faDatabase,
  faImages,
  faFile,
  faCrown,
  faCheck,
  faArrowUp,
  faInfoCircle,
  faRocket,
  faStar,
  faShieldAlt,
  faBolt,
  faTimes,
  faArrowRight,
  faChartLine,
  faBox,
  faCreditCard,
  faReceipt,
  faCalendar,
  faGem,
  faDownload,
  faEnvelope,
  faBan,
  faRobot,
  faLayerGroup,
  faCubes,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM_PLANS, PlatformPlanType } from '../types/plans';
import { api, billingAPI, trialAPI } from '../services/api';
import { usePlanUsage } from '../hooks/usePlanUsage';
import { useRBAC } from '../hooks/useRBAC';
import { useI18n } from '../hooks/useI18n';
import '../styles/SubscriptionHub.css';

type TabType = 'overview' | 'plans' | 'storage' | 'billing';

const SubscriptionHub = () => {
  const { t } = useI18n();
  const { currentWorkspace, workspaces } = useRBAC();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<PlatformPlanType>('FREE');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlatformPlanType | null>(null);

  // Trial state
  const [trialStatus, setTrialStatus] = useState<{
    trialActive: boolean;
    trialPlanId: string | null;
    daysRemaining: number;
    trialUsed: boolean;
    canStartTrial: boolean;
    trialEndDate: string | null;
  } | null>(null);
  const [trialLoading, setTrialLoading] = useState(false);

  // Storage data — use SI (decimal) units: 1 GB = 1,000,000,000 bytes so 300 MB = 300,000,000
  const [storageUsage, setStorageUsage] = useState({
    used: 0,
    total: 300 * 1000 * 1000, // 300 MB in SI bytes for FREE plan
    breakdown: { images: 0, documents: 0, other: 0 },
  });

  // Plan features summary
  const [itemCount, setItemCount] = useState(0);
  const [categoryCount, setCategoryCount] = useState(0);

  // Stripe invoices
  interface StripeInvoice {
    id: string;
    number: string | null;
    status: string;
    amount: number;
    currency: string;
    created: number;
    period_start: number;
    period_end: number;
    invoice_pdf: string | null;
    hosted_invoice_url: string | null;
    description: string | null;
  }
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Plan usage from API
  const { planUsage } = usePlanUsage();

  useEffect(() => {
    fetchData();
    fetchTrialStatus();
  }, []);

  useEffect(() => {
    if (activeTab === 'billing' && invoices.length === 0 && !invoicesLoading) {
      setInvoicesLoading(true);
      billingAPI.getInvoices()
        .then(res => setInvoices(res.data?.invoices || []))
        .catch(() => setInvoices([]))
        .finally(() => setInvoicesLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (planUsage?.plan?.id) {
      setCurrentPlan(planUsage.plan.id as PlatformPlanType);
      // Update storage total based on plan — use SI decimal units (1 GB = 1e9 bytes)
      const planFeatures = PLATFORM_PLANS[planUsage.plan.id as PlatformPlanType]?.features;
      if (planFeatures) {
        setStorageUsage(prev => ({ ...prev, total: planFeatures.storageGB * 1000 * 1000 * 1000 }));
      }
    }
  }, [planUsage]);

  const fetchData = async () => {
    try {
      const [itemsRes, catsRes, storageRes] = await Promise.all([
        api.get('/items?take=10000'),
        api.get('/categories'),
        api.get('/plan-usage/storage').catch(() => null),
      ]);
      
      const items = itemsRes.data.items || [];
      setItemCount(itemsRes.data.total || items.length);
      setCategoryCount(catsRes.data?.length || 0);

      if (storageRes?.data) {
        // Use real storage data from backend
        setStorageUsage(prev => ({
          ...prev,
          used: storageRes.data.total,
          breakdown: {
            images: storageRes.data.breakdown.images,
            documents: storageRes.data.breakdown.documents,
            other: storageRes.data.breakdown.metadata,
          },
        }));
      } else {
        // Fallback: estimate from item data (SI units)
        let imgSize = 0;
        items.forEach((item: any) => {
          const count = (item.imageUrl ? 1 : 0) + (item.images?.length || 0);
          imgSize += count * 512 * 1000; // ~512 KB per image (SI)
        });
        const otherSize = items.length * 8 * 1000; // ~8 KB metadata per item (SI)
        setStorageUsage(prev => ({
          ...prev,
          used: imgSize + otherSize,
          breakdown: { images: imgSize, documents: 0, other: otherSize },
        }));
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      // First check if trial expired (auto-downgrade)
      await trialAPI.checkExpiry();
      // Then get status
      const res = await trialAPI.getStatus();
      setTrialStatus(res.data);
    } catch {
      // Silently fail — trial feature is optional
    }
  };

  const handleStartTrial = async (planId: PlatformPlanType) => {
    if (trialLoading) return;
    try {
      setTrialLoading(true);
      setUpgradeError(null);
      await trialAPI.start(planId);
      await fetchTrialStatus();
      // Refresh plan data
      window.location.reload();
    } catch (err: any) {
      setUpgradeError(err?.response?.data?.error || 'Failed to start trial');
    } finally {
      setTrialLoading(false);
    }
  };

  // Format bytes using SI (decimal) units — matches advertised storage values
  const formatBytes = (bytes: number): string => {
    if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
    if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
    if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const usagePercent = Math.min(100, (storageUsage.used / storageUsage.total) * 100);
  const plan = PLATFORM_PLANS[currentPlan];

  const handleUpgrade = (planId: PlatformPlanType) => {
    if (planId === 'FREE' || planId === currentPlan) return;
    setSelectedPlan(planId);
    setUpgradeError(null);
    setShowPaymentModal(true);
  };

  const handleStripeCheckout = async (planId: PlatformPlanType) => {
    // Use current workspace or fall back to first available workspace
    const workspaceId = currentWorkspace?.id || workspaces?.[0]?.id;
    if (!workspaceId) {
      setUpgradeError('Please create a workspace first before subscribing.');
      return;
    }
    try {
      setUpgradeLoading(true);
      setUpgradeError(null);
      const res = await billingAPI.createCheckout({
        planId,
        interval: billingCycle === 'yearly' ? 'year' : 'month',
        workspaceId,
        successUrl: `${window.location.origin}/subscription?upgraded=true`,
        cancelUrl: `${window.location.origin}/subscription?canceled=true`,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setUpgradeError(t('subscription.checkoutError') || 'Failed to create checkout session.');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setUpgradeError(err?.response?.data?.error || t('subscription.checkoutError') || 'Failed to start checkout. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const navigateToExternal = (url: string) => {
    window.location.assign(url);
  };

  const openMailFallback = (subject: string) => {
    navigateToExternal('mailto:support@elvoaq.ch?subject=' + encodeURIComponent(subject));
  };

  const openBillingPortal = async (fallbackSubject: string) => {
    try {
      const res = await billingAPI.openPortal(window.location.href);
      if (res.data?.url) {
        navigateToExternal(res.data.url);
        return;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
    openMailFallback(fallbackSubject);
  };

  const downloadLatestInvoice = async () => {
    const shouldFetchInvoices = invoices.length === 0;
    try {
      let invoiceList = invoices;
      if (shouldFetchInvoices) {
        setInvoicesLoading(true);
        const res = await billingAPI.getInvoices();
        invoiceList = res.data?.invoices || [];
        setInvoices(invoiceList);
      }

      const latest = invoiceList[0];
      const invoiceUrl = latest?.invoice_pdf || latest?.hosted_invoice_url;
      if (invoiceUrl) {
        navigateToExternal(invoiceUrl);
        return;
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
    } finally {
      if (shouldFetchInvoices) {
        setInvoicesLoading(false);
      }
    }

    await openBillingPortal('Invoice Request');
  };

  const getPlanIcon = (planId: PlatformPlanType) => {
    switch (planId) {
      case 'FREE': return faRocket;
      case 'STARTER': return faStar;
      case 'COLLECTOR': return faGem;
      case 'PRO': return faBolt;
      case 'BUSINESS': return faShieldAlt;
      case 'ULTIMATE': return faCrown;
      case 'CUSTOM': return faCrown;
      default: return faRocket;
    }
  };

  const getBreakdown = () => {
    const total = storageUsage.breakdown.images + storageUsage.breakdown.documents + storageUsage.breakdown.other;
    if (total === 0) return [
      { label: t('subscription.images'), value: 0, color: '#6366f1', percent: 0, icon: faImages },
      { label: t('subscription.documents'), value: 0, color: '#8b5cf6', percent: 0, icon: faFile },
      { label: t('subscription.metadata'), value: 0, color: '#a855f7', percent: 0, icon: faDatabase },
    ];
    return [
      { label: t('subscription.images'), value: storageUsage.breakdown.images, color: '#6366f1', percent: (storageUsage.breakdown.images / total) * 100, icon: faImages },
      { label: t('subscription.documents'), value: storageUsage.breakdown.documents, color: '#8b5cf6', percent: (storageUsage.breakdown.documents / total) * 100, icon: faFile },
      { label: t('subscription.metadata'), value: storageUsage.breakdown.other, color: '#a855f7', percent: (storageUsage.breakdown.other / total) * 100, icon: faDatabase },
    ];
  };

  if (loading) {
    return <div className="sub-hub loading-state"><div className="spinner" /><p>{t('subscription.loading')}</p></div>;
  }

  return (
    <div className="sub-hub">
      {/* Header */}
      <div className="sub-hub-header">
        <div>
          <h1><FontAwesomeIcon icon={faCreditCard} /> {t('subscription.header')}</h1>
          <p>{t('subscription.headerDesc')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="sub-hub-tabs">
        {([
          { key: 'overview' as TabType, label: t('subscription.overview'), icon: faChartLine },
          { key: 'plans' as TabType, label: t('subscription.plans'), icon: faCrown },
          { key: 'storage' as TabType, label: t('subscription.storage'), icon: faCloud },
          { key: 'billing' as TabType, label: t('subscription.billing'), icon: faReceipt },
        ]).map(tab => (
          <button
            key={tab.key}
            className={`sub-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* === OVERVIEW TAB === */}
      {activeTab === 'overview' && (
        <div className="sub-overview">
          {/* Plan Header Card */}
          <div className="sub-plan-header-card">
            <div className="sub-plan-header-left">
              <div className="sub-stat-icon">
                <FontAwesomeIcon icon={getPlanIcon(currentPlan)} />
              </div>
              <div className="sub-stat-info">
                <div className="sub-stat-label">{t('subscription.currentPlan')}</div>
                <div className="sub-stat-value">{plan.name}</div>
                <div className="sub-stat-sub">{t(`subscription.planTagline.${currentPlan}`) || plan.tagline}</div>
              </div>
            </div>
            {currentPlan === 'FREE' && (
              <button className="btn-upgrade-small" onClick={() => setActiveTab('plans')}>
                <FontAwesomeIcon icon={faArrowUp} /> {t('subscription.upgrade')}
              </button>
            )}
            {currentPlan !== 'FREE' && (
              <button
                className="btn-upgrade-small btn-manage-subscription"
                onClick={() => openBillingPortal('Manage Subscription')}
              >
                <FontAwesomeIcon icon={faCreditCard} /> {t('subscription.manageStripe')}
              </button>
            )}
          </div>

          {/* Trial Banner */}
          {trialStatus?.trialActive && (
            <div className="sub-trial-banner active">
              <div className="sub-trial-info">
                <FontAwesomeIcon icon={faRocket} />
                <div>
                  <strong>{t('subscription.trialActive')} — {trialStatus.trialPlanId} Plan</strong>
                  <p>{trialStatus.daysRemaining} {t('subscription.trialDaysLeft')}</p>
                </div>
              </div>
              <div className="sub-trial-actions">
                <button className="btn-upgrade-small" onClick={() => setActiveTab('plans')}>
                  <FontAwesomeIcon icon={faArrowUp} /> {t('subscription.upgradeNow')}
                </button>
                <button className="btn-cancel-trial" onClick={async () => {
                  if (!window.confirm(t('subscription.cancelTrialConfirm'))) return;
                  try {
                    await trialAPI.cancel();
                    window.location.reload();
                  } catch (err: any) {
                    alert(err.response?.data?.error || 'Failed to cancel trial');
                  }
                }}>
                  {t('subscription.cancelTrial')}
                </button>
              </div>
            </div>
          )}
          {trialStatus && !trialStatus.trialActive && trialStatus.canStartTrial && currentPlan === 'FREE' && (
            <div className="sub-trial-banner available">
              <div className="sub-trial-info">
                <FontAwesomeIcon icon={faRocket} />
                <div>
                  <strong>{t('subscription.trialAvailable')}</strong>
                  <p>{t('subscription.trialAvailableDesc')}</p>
                </div>
              </div>
              <button className="btn-upgrade-small" onClick={() => setActiveTab('plans')}>
                {t('subscription.startTrial')} <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          )}

          {/* Usage Overview with Progress Bars */}
          <div className="sub-usage-section">
            <h3 className="sub-usage-title">{t('subscription.usageOverview')}</h3>
            <div className="sub-usage-grid">
              {/* Items */}
              <div className="sub-usage-card">
                <div className="sub-usage-card-header">
                  <div className="sub-usage-icon items-icon"><FontAwesomeIcon icon={faBox} /></div>
                  <div className="sub-usage-label">{t('subscription.items')}</div>
                  <div className="sub-usage-count">
                    {planUsage?.usage?.items?.current ?? itemCount}
                    <span className="sub-usage-limit">/ {plan.features.maxItems === -1 ? '∞' : plan.features.maxItems}</span>
                  </div>
                </div>
                {plan.features.maxItems !== -1 && (
                  <div className="sub-usage-bar">
                    <div className="sub-usage-fill" style={{ width: `${Math.min(100, ((planUsage?.usage?.items?.current ?? itemCount) / plan.features.maxItems) * 100)}%` }}
                      data-status={((planUsage?.usage?.items?.current ?? itemCount) / plan.features.maxItems) >= 1 ? 'full' : ((planUsage?.usage?.items?.current ?? itemCount) / plan.features.maxItems) >= 0.8 ? 'warning' : 'ok'}
                    />
                  </div>
                )}
              </div>

              {/* Categories */}
              <div className="sub-usage-card">
                <div className="sub-usage-card-header">
                  <div className="sub-usage-icon cat-icon"><FontAwesomeIcon icon={faLayerGroup} /></div>
                  <div className="sub-usage-label">{t('subscription.categories')}</div>
                  <div className="sub-usage-count">
                    {planUsage?.usage?.categories?.current ?? categoryCount}
                    <span className="sub-usage-limit">/ {plan.features.maxCategories === -1 ? '∞' : plan.features.maxCategories}</span>
                  </div>
                </div>
                {plan.features.maxCategories !== -1 && (
                  <div className="sub-usage-bar">
                    <div className="sub-usage-fill" style={{ width: `${Math.min(100, ((planUsage?.usage?.categories?.current ?? categoryCount) / plan.features.maxCategories) * 100)}%` }}
                      data-status={((planUsage?.usage?.categories?.current ?? categoryCount) / plan.features.maxCategories) >= 1 ? 'full' : ((planUsage?.usage?.categories?.current ?? categoryCount) / plan.features.maxCategories) >= 0.8 ? 'warning' : 'ok'}
                    />
                  </div>
                )}
              </div>

              {/* Workspaces */}
              <div className="sub-usage-card">
                <div className="sub-usage-card-header">
                  <div className="sub-usage-icon ws-icon"><FontAwesomeIcon icon={faCubes} /></div>
                  <div className="sub-usage-label">{t('subscription.workspaces')}</div>
                  <div className="sub-usage-count">
                    {planUsage?.usage?.workspaces?.current ?? 1}
                    <span className="sub-usage-limit">/ {plan.features.maxWorkspaces === -1 ? '∞' : plan.features.maxWorkspaces}</span>
                  </div>
                </div>
                {plan.features.maxWorkspaces !== -1 && (
                  <div className="sub-usage-bar">
                    <div className="sub-usage-fill" style={{ width: `${Math.min(100, ((planUsage?.usage?.workspaces?.current ?? 1) / plan.features.maxWorkspaces) * 100)}%` }}
                      data-status={((planUsage?.usage?.workspaces?.current ?? 1) / plan.features.maxWorkspaces) >= 1 ? 'full' : 'ok'}
                    />
                  </div>
                )}
              </div>

              {/* AI Chat Messages */}
              <div className="sub-usage-card">
                <div className="sub-usage-card-header">
                  <div className="sub-usage-icon ai-icon"><FontAwesomeIcon icon={faRobot} /></div>
                  <div className="sub-usage-label">{t('subscription.aiMessages')}</div>
                  <div className="sub-usage-count">
                    {planUsage?.usage?.aiChatMessages?.current ?? 0}
                    <span className="sub-usage-limit">/ {plan.features.aiChatMessagesPerMonth === -1 ? '∞' : `${plan.features.aiChatMessagesPerMonth}${t('subscription.perMonth')}`}</span>
                  </div>
                </div>
                {plan.features.aiChatMessagesPerMonth !== -1 && (
                  <div className="sub-usage-bar">
                    <div className="sub-usage-fill" style={{ width: `${Math.min(100, ((planUsage?.usage?.aiChatMessages?.current ?? 0) / plan.features.aiChatMessagesPerMonth) * 100)}%` }}
                      data-status={((planUsage?.usage?.aiChatMessages?.current ?? 0) / plan.features.aiChatMessagesPerMonth) >= 1 ? 'full' : ((planUsage?.usage?.aiChatMessages?.current ?? 0) / plan.features.aiChatMessagesPerMonth) >= 0.8 ? 'warning' : 'ok'}
                    />
                  </div>
                )}
              </div>

              {/* Storage */}
              <div className="sub-usage-card">
                <div className="sub-usage-card-header">
                  <div className="sub-usage-icon storage-icon"><FontAwesomeIcon icon={faCloud} /></div>
                  <div className="sub-usage-label">{t('subscription.storageLabel')}</div>
                  <div className="sub-usage-count">
                    {formatBytes(storageUsage.used)}
                    <span className="sub-usage-limit">/ {formatBytes(storageUsage.total)}</span>
                  </div>
                </div>
                <div className="sub-usage-bar">
                  <div className="sub-usage-fill" style={{ width: `${usagePercent}%` }}
                    data-status={usagePercent >= 90 ? 'full' : usagePercent >= 75 ? 'warning' : 'ok'}
                  />
                </div>
              </div>

              {/* Images per Item */}
              <div className="sub-usage-card">
                <div className="sub-usage-card-header">
                  <div className="sub-usage-icon img-icon"><FontAwesomeIcon icon={faImages} /></div>
                  <div className="sub-usage-label">{t('subscription.imagesPerItem')}</div>
                  <div className="sub-usage-count">
                    <span className="sub-usage-limit">{plan.features.maxImagesPerItem === -1 ? '∞' : plan.features.maxImagesPerItem} max</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Current Plan Features */}
          <div className="sub-features-card">
            <h3>{t('subscription.yourPlanFeatures')}</h3>
            <div className="sub-features-grid">
              <div className="sub-feature">
                <FontAwesomeIcon icon={faCheck} className="check" />
                <span>{plan.features.maxItems === -1 ? t('subscription.unlimited') : plan.features.maxItems} {t('subscription.itemsLabel')}</span>
              </div>
              <div className="sub-feature">
                <FontAwesomeIcon icon={faCheck} className="check" />
                <span>{plan.features.maxCategories === -1 ? t('subscription.unlimited') : plan.features.maxCategories} {t('subscription.customCategories')}</span>
              </div>
              <div className="sub-feature">
                <FontAwesomeIcon icon={faCheck} className="check" />
                <span>{plan.features.storageGB < 1 ? `${Math.round(plan.features.storageGB * 1000)} MB` : `${plan.features.storageGB} GB`} {t('subscription.storageLabel')}</span>
              </div>
              <div className="sub-feature">
                <FontAwesomeIcon icon={faCheck} className="check" />
                <span>{plan.features.maxImagesPerItem === -1 ? t('subscription.unlimited') : plan.features.maxImagesPerItem} {t('subscription.imagesPerItemLabel')}</span>
              </div>
              <div className="sub-feature">
                <FontAwesomeIcon icon={faCheck} className="check" />
                <span>{plan.features.maxWorkspaces === -1 ? t('subscription.unlimited') : plan.features.maxWorkspaces} {t('subscription.workspacesLabel')}</span>
              </div>
              <div className="sub-feature">
                <FontAwesomeIcon icon={faCheck} className="check" />
                <span>{plan.features.aiChatMessagesPerMonth === -1 ? t('subscription.unlimited') : plan.features.aiChatMessagesPerMonth} {t('subscription.aiMessagesMonth')}</span>
              </div>
              <div className={`sub-feature ${!plan.features.aiCategorization ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={plan.features.aiCategorization ? faCheck : faTimes} className={plan.features.aiCategorization ? 'check' : 'no'} />
                <span>{t('subscription.aiCategorization')}</span>
              </div>
              <div className={`sub-feature ${!plan.features.aiValuePrediction ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={plan.features.aiValuePrediction ? faCheck : faTimes} className={plan.features.aiValuePrediction ? 'check' : 'no'} />
                <span>{t('subscription.valuePrediction')}</span>
              </div>
              <div className={`sub-feature ${!plan.features.advancedAnalytics ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={plan.features.advancedAnalytics ? faCheck : faTimes} className={plan.features.advancedAnalytics ? 'check' : 'no'} />
                <span>{t('subscription.advancedAnalytics')}</span>
              </div>
              <div className={`sub-feature ${!plan.features.prioritySupport ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={plan.features.prioritySupport ? faCheck : faTimes} className={plan.features.prioritySupport ? 'check' : 'no'} />
                <span>{t('subscription.prioritySupport')}</span>
              </div>
            </div>
            <button className="btn-see-plans" onClick={() => setActiveTab('plans')}>
              {t('subscription.compareAllPlans')} <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>

          {/* Storage Warning */}
          {usagePercent > 75 && (
            <div className="sub-warning">
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>{t('subscription.storageWarningPrefix')} {usagePercent.toFixed(0)}% {t('subscription.storageWarningSuffix')}</span>
              <button onClick={() => setActiveTab('storage')}>{t('subscription.manageStorage')}</button>
            </div>
          )}
        </div>
      )}

      {/* === PLANS TAB === */}
      {activeTab === 'plans' && (
        <div className="sub-plans">
          <div className="billing-toggle-bar">
            <button className={billingCycle === 'monthly' ? 'active' : ''} onClick={() => setBillingCycle('monthly')}>{t('subscription.monthly')}</button>
            <button className={billingCycle === 'yearly' ? 'active' : ''} onClick={() => setBillingCycle('yearly')}>
              {t('subscription.yearly')} <span className="save-pill">{t('subscription.save20')}</span>
            </button>
          </div>

          <div className="sub-plans-grid">
            {Object.values(PLATFORM_PLANS).map((p) => (
              <div key={p.id} className={`sub-plan-card ${p.id === currentPlan ? 'current' : ''} ${p.popular ? 'popular' : ''}`}>
                {p.popular && <div className="popular-pill"><FontAwesomeIcon icon={faStar} /> {t('subscription.mostPopular')}</div>}
                {p.id === currentPlan && <div className="current-pill">{t('subscription.currentPlanLabel')}</div>}

                <div className="sub-plan-header">
                  <FontAwesomeIcon icon={getPlanIcon(p.id)} className="plan-icon-large" />
                  <h3>{p.name}</h3>
                  <p className="plan-tagline">{t(`subscription.planTagline.${p.id}`) || p.tagline}</p>
                </div>

                <div className="sub-plan-price">
                  {p.monthlyPrice === 0 ? (
                    <span className="price-free">{t('subscription.free')}</span>
                  ) : p.monthlyPrice === -1 ? (
                    <span className="price-custom">{t('subscription.custom')}</span>
                  ) : (
                    <>
                      <span className="price-amount">€{billingCycle === 'monthly' ? p.monthlyPrice : Math.round(p.yearlyPrice / 12)}</span>
                      <span className="price-period">{t('subscription.perMonth')}</span>
                    </>
                  )}
                </div>

                <ul className="sub-plan-features">
                  {p.highlights.map((h: string, i: number) => (
                    <li key={i}><FontAwesomeIcon icon={faCheck} /> {t(`subscription.planHighlight.${p.id}.${i}`) || h}</li>
                  ))}
                </ul>

                <button
                  className={`sub-plan-cta ${p.id === currentPlan ? 'current' : ''}`}
                  disabled={p.id === currentPlan || p.id === 'FREE'}
                  onClick={() => handleUpgrade(p.id)}
                >
                  {p.id === currentPlan ? t('subscription.currentPlanLabel') : p.id === 'FREE' ? t('subscription.free') : p.monthlyPrice === -1 ? t('subscription.contactSales') : p.monthlyPrice > (PLATFORM_PLANS[currentPlan]?.monthlyPrice || 0) ? t('subscription.upgrade') : t('subscription.switch')}
                </button>

                {/* Free Trial Button */}
                {p.id !== 'FREE' && p.id !== currentPlan && p.monthlyPrice !== -1 && trialStatus?.canStartTrial && !trialStatus?.trialActive && (
                  <button
                    className="sub-plan-trial-btn"
                    disabled={trialLoading}
                    onClick={() => handleStartTrial(p.id)}
                  >
                    <FontAwesomeIcon icon={faRocket} /> {trialLoading ? (t('common.loading') || 'Loading…') : t('subscription.startFreeTrial')}
                  </button>
                )}
                {trialStatus?.trialActive && trialStatus?.trialPlanId === p.id && (
                  <div className="sub-plan-trial-active">
                    <FontAwesomeIcon icon={faRocket} /> {t('subscription.trialActiveLabel')} — {trialStatus.daysRemaining} {t('subscription.trialDaysLeft')}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Compare All Features Table */}
          <div className="sub-compare-table">
            <h2><FontAwesomeIcon icon={faChartLine} /> {t('subscription.compareFeatures')}</h2>
            <div className="compare-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>{t('subscription.feature')}</th>
                    <th>{PLATFORM_PLANS.FREE.name}</th>
                    <th>{PLATFORM_PLANS.STARTER.name}</th>
                    <th>{PLATFORM_PLANS.COLLECTOR.name}</th>
                    <th>{PLATFORM_PLANS.PRO.name}</th>
                    <th>{PLATFORM_PLANS.BUSINESS.name}</th>
                    <th>{PLATFORM_PLANS.ULTIMATE.name}</th>
                    <th>{t('subscription.custom')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="compare-section-row"><td colSpan={8}>{t('subscription.coreLimits')}</td></tr>
                  <tr>
                    <td>{t('subscription.items')}</td>
                    <td>30</td><td>250</td><td>1,000</td><td>10,000</td><td>∞</td><td>∞</td><td>∞</td>
                  </tr>
                  <tr>
                    <td>{t('subscription.storageLabel')}</td>
                    <td>300 MB</td><td>2 GB</td><td>10 GB</td><td>50 GB</td><td>200 GB</td><td>1 TB</td><td>{t('subscription.custom')}</td>
                  </tr>
                  <tr>
                    <td>{t('subscription.imagesPerItem')}</td>
                    <td>5</td><td>10</td><td>20</td><td>40</td><td>60</td><td>∞</td><td>∞</td>
                  </tr>
                  <tr>
                    <td>{t('subscription.workspaces')}</td>
                    <td>1</td><td>2</td><td>5</td><td>15</td><td>∞</td><td>∞</td><td>∞</td>
                  </tr>
                  <tr>
                    <td>{t('subscription.teamMembers')}</td>
                    <td>1</td><td>1</td><td>3</td><td>10</td><td>25</td><td>∞</td><td>∞</td>
                  </tr>
                  <tr className="compare-section-row"><td colSpan={8}>{t('subscription.aiFeatures')}</td></tr>
                  <tr>
                    <td>{t('subscription.aiCategorization')}</td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr>
                    <td>{t('subscription.valuePrediction')}</td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr>
                    <td>{t('subscription.duplicateDetect')}</td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr>
                    <td>{t('subscription.aiReportsMonth')}</td>
                    <td>0</td><td>5</td><td>25</td><td>∞</td><td>∞</td><td>∞</td><td>∞</td>
                  </tr>
                  <tr className="compare-section-row"><td colSpan={8}>{t('subscription.security')}</td></tr>
                  <tr>
                    <td>{t('subscription.twoFactorAuth')}</td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr>
                    <td>{t('subscription.auditLogs')}</td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr>
                    <td>{t('subscription.sso')}</td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr className="compare-section-row"><td colSpan={8}>{t('subscription.support')}</td></tr>
                  <tr>
                    <td>{t('subscription.emailSupportLabel')}</td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr>
                    <td>{t('subscription.prioritySupport')}</td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                  <tr>
                    <td>{t('subscription.dedicatedManager')}</td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faTimes} className="feat-no" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                    <td><FontAwesomeIcon icon={faCheck} className="feat-yes" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="sub-faq">
            <h2>{t('subscription.faqTitle')}</h2>
            <div className="faq-list">
              <details className="faq-item">
                <summary>{t('subscription.faqSwitchQ')}</summary>
                <p>{t('subscription.faqSwitchA')}</p>
              </details>
              <details className="faq-item">
                <summary>{t('subscription.faqDowngradeQ')}</summary>
                <p>{t('subscription.faqDowngradeA')}</p>
              </details>
              <details className="faq-item">
                <summary>{t('subscription.faqRefundQ')}</summary>
                <p>{t('subscription.faqRefundA')}</p>
              </details>
              <details className="faq-item">
                <summary>{t('subscription.faqPaymentQ')}</summary>
                <p>{t('subscription.faqPaymentA')}</p>
              </details>
              <details className="faq-item">
                <summary>{t('subscription.faqTrialQ')}</summary>
                <p>{t('subscription.faqTrialA')}</p>
              </details>
              <details className="faq-item">
                <summary>{t('subscription.faqStorageQ')}</summary>
                <p>{t('subscription.faqStorageA')}</p>
              </details>
            </div>
          </div>

          {/* CTA */}
          <div className="sub-cta">
            <h2>{t('subscription.ctaTitle')}</h2>
            <p>{t('subscription.ctaDesc')}</p>
            <div className="cta-buttons">
              <button className="btn-cta-primary" onClick={() => {
                setActiveTab('overview');
              }}>
                <FontAwesomeIcon icon={faRocket} /> {t('subscription.startFree')}
              </button>
              <a href="mailto:support@elvoaq.ch?subject=Contact%20Us" className="btn-cta-secondary" style={{ textDecoration: 'none' }}>
                <FontAwesomeIcon icon={faEnvelope} /> {t('subscription.contactUs')}
              </a>
            </div>
          </div>

        </div>
      )}

      {/* === STORAGE TAB === */}
      {activeTab === 'storage' && (
        <div className="sub-storage">
          <div className="sub-storage-main">
            {/* Storage Circle */}
            <div className="sub-storage-visual">
              <div className="storage-ring-container">
                <svg viewBox="0 0 120 120" className="storage-ring-svg">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(102,126,234,0.1)" strokeWidth="10" />
                  <circle cx="60" cy="60" r="50" fill="none" stroke="url(#subGrad)" strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={`${usagePercent * 3.14} 314`} transform="rotate(-90 60 60)" />
                  <defs>
                    <linearGradient id="subGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#667eea" />
                      <stop offset="100%" stopColor="#764ba2" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="storage-ring-center">
                  <span className="storage-pct">{usagePercent.toFixed(0)}%</span>
                  <span className="storage-label-small">{t('subscription.used')}</span>
                </div>
              </div>
              <div className="storage-summary-nums">
                <div><span className="num">{formatBytes(storageUsage.used)}</span><span className="lbl">{t('subscription.used')}</span></div>
                <div className="divider" />
                <div><span className="num">{formatBytes(storageUsage.total - storageUsage.used)}</span><span className="lbl">{t('subscription.freeSpace')}</span></div>
                <div className="divider" />
                <div><span className="num">{formatBytes(storageUsage.total)}</span><span className="lbl">{t('subscription.total')}</span></div>
              </div>
            </div>

            {/* Breakdown */}
            <div className="sub-storage-breakdown">
              <h3>{t('subscription.storageBreakdown')}</h3>
              <div className="breakdown-bar-large">
                {getBreakdown().map(b => (
                  <div key={b.label} className="breakdown-seg" style={{ width: `${Math.max(b.percent, 2)}%`, background: b.color }} title={`${b.label}: ${formatBytes(b.value)}`} />
                ))}
              </div>
              <div className="breakdown-items">
                {getBreakdown().map(b => (
                  <div key={b.label} className="breakdown-row">
                    <div className="breakdown-icon" style={{ color: b.color }}><FontAwesomeIcon icon={b.icon} /></div>
                    <div className="breakdown-name">{b.label}</div>
                    <div className="breakdown-size">{formatBytes(b.value)}</div>
                    <div className="breakdown-pct">{b.percent.toFixed(0)}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Storage — Contact Us */}
          <div className="sub-storage-addons">
            <h3><FontAwesomeIcon icon={faCloud} /> {t('subscription.needMoreSpace')}</h3>
            <p>{t('subscription.addExtraStorage')}</p>
            <div style={{ marginTop: '16px' }}>
              <a
                href="mailto:support@elvoaq.ch?subject=Storage%20Add-on%20Inquiry"
                className="addon-btn"
                style={{ textDecoration: 'none', textAlign: 'center', display: 'inline-block', padding: '10px 24px', borderRadius: '10px' }}
              >
                {t('subscription.contactForAddon')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* === BILLING TAB === */}
      {activeTab === 'billing' && (
        <div className="sub-billing">
          <div className="billing-card">
            <div className="billing-header">
              <h3><FontAwesomeIcon icon={faReceipt} /> {t('subscription.billingSummary')}</h3>
            </div>
            <div className="billing-details">
              <div className="billing-row">
                <span className="billing-label">{t('subscription.currentPlan')}</span>
                <span className="billing-value">{plan.name}</span>
              </div>
              <div className="billing-row">
                <span className="billing-label">{t('subscription.billingCycle')}</span>
                <span className="billing-value">{billingCycle === 'yearly' ? t('subscription.yearly') : t('subscription.monthly')}</span>
              </div>
              <div className="billing-row">
                <span className="billing-label">{billingCycle === 'yearly' ? t('subscription.yearlyCost') : t('subscription.monthlyCost')}</span>
                <span className="billing-value">
                  {plan.monthlyPrice === -1 ? t('subscription.custom') : plan.monthlyPrice === 0 ? t('subscription.free') : billingCycle === 'yearly' ? `€${plan.yearlyPrice}${t('subscription.perYear')}` : `€${plan.monthlyPrice}${t('subscription.perMonth')}`}
                </span>
              </div>
              <div className="billing-row">
                <span className="billing-label">{t('subscription.nextBillingDate')}</span>
                <span className="billing-value">
                  <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 6 }} />
                  {plan.monthlyPrice === 0 ? t('subscription.naFree') : new Date(Date.now() + (billingCycle === 'yearly' ? 365 : 30) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div className="billing-row">
                <span className="billing-label">{t('subscription.paymentMethod')}</span>
                <span className="billing-value">{plan.monthlyPrice === 0 ? t('subscription.naFree') : 'Stripe'}</span>
              </div>
            </div>
            <div className="billing-actions">
              <button className="btn-secondary" onClick={() => openBillingPortal('Billing Support')}>
                <FontAwesomeIcon icon={faCreditCard} /> {t('subscription.manageStripe')}
              </button>
              <button className="btn-secondary" onClick={downloadLatestInvoice}>
                <FontAwesomeIcon icon={faDownload} /> {t('subscription.downloadInvoice')}
              </button>
            </div>
          </div>

          {/* Cancel Subscription */}
          {currentPlan !== 'FREE' && (
            <div className="billing-cancel-card">
              <h3><FontAwesomeIcon icon={faBan} /> {t('subscription.cancelSubscription')}</h3>
              <p>{t('subscription.cancelDesc')}</p>
              <button className="btn-cancel" onClick={() => openBillingPortal('Cancel Subscription')}>
                {t('subscription.cancelStripe')}
              </button>
            </div>
          )}

          <div className="billing-history-card">
            <h3>{t('subscription.paymentHistory')}</h3>
            {invoicesLoading ? (
              <div className="billing-empty"><FontAwesomeIcon icon={faReceipt} /><p>{t('common.loading') || 'Loading…'}</p></div>
            ) : invoices.length === 0 ? (
              <div className="billing-empty">
                <FontAwesomeIcon icon={faReceipt} />
                <p>{t('subscription.noPayments')}</p>
                <span>{t('subscription.noPaymentsDesc')}</span>
              </div>
            ) : (
              <div className="invoice-list">
                {invoices.map(inv => (
                  <div key={inv.id} className="invoice-row">
                    <div className="invoice-info">
                      <span className="invoice-number">{inv.number || inv.id}</span>
                      <span className="invoice-date">{new Date(inv.created * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    </div>
                    <div className="invoice-right">
                      <span className="invoice-amount">
                        {new Intl.NumberFormat(undefined, { style: 'currency', currency: (inv.currency || 'eur').toUpperCase() }).format(inv.amount / 100)}
                      </span>
                      <span className={`invoice-status invoice-status-${inv.status}`}>{inv.status}</span>
                      {(inv.invoice_pdf || inv.hosted_invoice_url) && (
                        <a
                          className="invoice-download-btn"
                          href={inv.invoice_pdf || inv.hosted_invoice_url!}
                          title={t('subscription.downloadInvoice') || 'Download invoice'}
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="billing-security">
            <FontAwesomeIcon icon={faEnvelope} />
            <div>
              <strong>{t('subscription.needHelpBilling')}</strong>
              <p>{t('subscription.contactBilling')} <a href="mailto:support@elvoaq.ch" style={{ color: '#667eea' }}>support@elvoaq.ch</a> {t('subscription.billingQuestions')}</p>
            </div>
          </div>

          <div className="billing-security">
            <FontAwesomeIcon icon={faShieldAlt} />
            <div>
              <strong>{t('subscription.securePayments')}</strong>
              <p>{t('subscription.securePaymentsDesc')}</p>
            </div>
          </div>
        </div>
      )}

      {/* === PAYMENT MODAL === */}
      {showPaymentModal && selectedPlan && (
        <div className="payment-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPaymentModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <div className="payment-modal-header">
              <FontAwesomeIcon icon={getPlanIcon(selectedPlan)} className="modal-plan-icon" />
              <h2>{t('subscription.upgradeTo')} {PLATFORM_PLANS[selectedPlan].name}</h2>
              <p>{t(`subscription.planDesc.${selectedPlan}`) || PLATFORM_PLANS[selectedPlan].tagline}</p>
            </div>

            <div className="payment-modal-price">
              {PLATFORM_PLANS[selectedPlan].monthlyPrice === -1 ? (
                <div className="price-display">
                  <span className="amount" style={{ fontSize: 28 }}>{t('subscription.customPricing')}</span>
                </div>
              ) : (
                <>
                  <div className="price-display">
                    <span className="currency">€</span>
                    <span className="amount">
                      {billingCycle === 'monthly' 
                        ? PLATFORM_PLANS[selectedPlan].monthlyPrice 
                        : PLATFORM_PLANS[selectedPlan].yearlyPrice}
                    </span>
                    <span className="period">{billingCycle === 'monthly' ? t('subscription.perMonth') : t('subscription.perYear')}</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <div className="save-badge">{t('subscription.save20Yearly')}</div>
                  )}
                </>
              )}
            </div>

            <div className="payment-modal-features">
              <h4>{t('subscription.whatsIncluded')}</h4>
              <ul>
                {PLATFORM_PLANS[selectedPlan].highlights.map((h: string, i: number) => (
                  <li key={i}>
                    <FontAwesomeIcon icon={faCheck} />
                    {t(`subscription.planHighlight.${selectedPlan}.${i}`) || h}
                  </li>
                ))}
              </ul>
            </div>

            <div className="payment-modal-checkout">
              {PLATFORM_PLANS[selectedPlan].monthlyPrice === -1 ? (
                <>
                  <a
                    href="mailto:support@elvoaq.ch?subject=Custom%20Plan%20Inquiry&body=Hi%2C%20I%20am%20interested%20in%20a%20custom%20plan.%20Please%20contact%20me%20with%20more%20details."
                    className="stripe-upgrade-btn"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      padding: '14px 28px', borderRadius: 12, fontWeight: 600, fontSize: 16,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff', textDecoration: 'none', border: 'none', cursor: 'pointer',
                      width: '100%', boxSizing: 'border-box',
                    }}
                  >
                    <FontAwesomeIcon icon={faEnvelope} /> {t('subscription.contactSales')}
                  </a>
                  <p style={{ textAlign: 'center', marginTop: 12, fontSize: 13, opacity: 0.7 }}>
                    {t('subscription.contactSalesDesc')}
                  </p>
                </>
              ) : (
                <>
                  {upgradeError && (
                    <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, color: '#f87171', fontSize: 13 }}>
                      {upgradeError}
                    </div>
                  )}
                  <button
                    onClick={() => handleStripeCheckout(selectedPlan)}
                    disabled={upgradeLoading}
                    className="stripe-upgrade-btn"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      padding: '14px 28px', borderRadius: 12, fontWeight: 600, fontSize: 16,
                      background: upgradeLoading ? '#475569' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff', textDecoration: 'none', border: 'none', cursor: upgradeLoading ? 'not-allowed' : 'pointer',
                      width: '100%', boxSizing: 'border-box',
                      opacity: upgradeLoading ? 0.7 : 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faCreditCard} /> {upgradeLoading ? (t('subscription.redirecting') || 'Redirecting to Stripe...') : t('subscription.subscribeStripe')}
                  </button>
                  <p style={{ textAlign: 'center', marginTop: 12, fontSize: 13, opacity: 0.7 }}>
                    {t('subscription.afterPaymentStripe')}
                  </p>
                </>
              )}
            </div>

            <div className="payment-modal-footer">
              <p>
                <FontAwesomeIcon icon={faShieldAlt} />
                {t('subscription.secureViaStripe')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionHub;
